import Vue from 'vue';
import axios from 'axios';
import Notify from 'quasar/src/plugins/Notify.js';;
import store from '../store/index.js';
import router from '../router/index.js';
import { compactObject, downloadProgress, uploadProgress } from './utils.js';
import { debounce } from 'lodash';
import i18next from 'i18next';

/**
 * @typedef {{loaded: number;total: number;}} ProgressEvent
 */

export const httpConfig = ({ version }) => {
  const gotedoLocale = store.getters['gotedoLocale']();
  const developmentToken = window.localStorage.getItem('development_token');
  const inStagingMode = process.env.VUE_APP_IN_STAGING_MODE === 'true';

  return {
    baseURL: getBaseUrl({ version }),
    timeout: store.getters['getHttpOptions']().timeout,
    headers: compactObject({
      ...store.getters['getHttpOptions']().headers,
      'X-Gotedo-Language': gotedoLocale ?? '',
      'X-Gotedo-Development-Token': inStagingMode ? developmentToken : undefined,
    }),
  };
};

/**
 *
 * @param {Object} payload - The payload object
 * @param {import('axios').AxiosError<{data: any | data: any[]}>} payload.errors
 * @param {boolean} payload.isValidationError
 * @param {Object} payload.config
 *
 * @return {Array<{string | false}>}
 */
export const getErrorArray = ({ errors, isValidationError, config }) => {
  return errors.map(({ message }) => {
    if (isValidationError) {
      if (config?.hideValidationErrors ?? false) {
        return false;
      }
      return i18next.t('http_errors.validation_error_with_message', { ns: 'general', message });
    }

    return message;
  });
};

function getBaseUrl({ version = 'v1' }) {
  const baseURL = store.getters['getHttpOptions']().baseURL.replace(/\/$/, '');
  return baseURL + `/${version}`;
}

/**
 *
 * @param {import('axios').AxiosResponse} response
 * @returns
 */
export function httpResponseHandler(response) {
  if (response.headers?.['x-is-notifiable']?.toLowerCase?.() !== 'on') {
    return response;
  }

  const message =
    typeof response.data === 'string'
      ? response.data
      : typeof response.data?.message === 'string'
      ? response.data.message
      : typeof response.data?.data === 'string'
      ? response.data.data
      : typeof response.data?.data?.message === 'string'
      ? response.data.data.message
      : '';

  if (message) {
    Notify.create({
      message,
      type: 'positive',
    });
  }

  return response;
}

export async function httpErrorHandler(error) {
  const config = error?.config || {};
  const dontNotify = 'notifiable' in config && config.notifiable === false;

  let message = '';
  let isOriginNotAllowedException = false;

  if (error.response?.headers?.['content-type']?.includes('text/plain')) {
    message = error.response.data.replace(
      /(?:E_ORIGIN_NOT_ALLOWED_EXCEPTION){0,1}:{0,1}\s{0,1}/,
      ''
    );
    isOriginNotAllowedException = error.response.data.includes('E_ORIGIN_NOT_ALLOWED_EXCEPTION');
  }

  // Handle error during file downloads
  if (error?.response?.data instanceof Blob) {
    // Convert the Blob to text and parse to JSON
    // Assign the JSON to the error data object and let go
    error.response.data = JSON.parse(await error?.response?.data.text());
  }

  const debouncedLogout = debounce(logout, 1000);

  async function logout(noLogoutRequest = false, silent = false) {
    await store.dispatch('auth/logoutUser', {
      message: i18next.t('http_errors.session_has_expired_log_in_again', { ns: 'general' }),
      dontNotify: dontNotify || silent,
      noLogoutRequest,
    });

    Vue.nextTick(() => {
      if (!store.getters['auth/getPushedToLogin']) {
        router.push({ name: 'Login' }).catch(() => {
          // Suppress redundant navigation error
        });
        store.commit('auth/setPushedToLogin', true);
      }
    });
  }

  if (error.message.includes('timeout') && error.message.includes('exceeded')) {
    message = i18next.t('http_errors.operation_timed_out_error', { ns: 'general' });

    Notify.create({
      message,
      type: 'negative',
      timeout: 0,
    });
    return Promise.reject(error);
  }

  if (error?.response?.status === 401) {
    const isFromAuthForm = !!config?.isFromAuthForm;

    // AuthForm endpoints have their own error handling
    if (isFromAuthForm) {
      return Promise.reject(error);
    }

    if (message) {
      Notify.create({
        message,
        type: 'negative',
        timeout: 0,
      });
    }

    if (error.config.redirectToLogin === false) {
      return Promise.reject(error);
    }

    // CRITICAL: Avoids infinite logout loop
    if (config.url === 'auth/login' && config.method.toLowerCase() === 'delete') {
      return Promise.reject(error);
    }

    if (!isOriginNotAllowedException) {
      // If the request `isFromAuthForm`, error will be handled
      // by `auth.js > onLoginError` function
      debouncedLogout(isFromAuthForm, isFromAuthForm);
    } else {
      // Simply push visitor to the Login page
      debouncedLogout(true, true);
    }

    return Promise.reject(error);
  }

  if (error?.response?.status === 404) {
    if (config.logoutOn404) {
      return await logout(true);
    }
  }

  if (Array.isArray(error.response?.data?.errors)) {
    getErrorArray({
      errors: error.response.data.errors,
      isValidationError: error.response?.status === 422,
      config,
    }).forEach((message) => {
      if (message === false) {
        return;
      }

      Notify.create({
        message,
        type: 'negative',
        timeout: 0,
      });
    });
  } else if (Array.isArray(error.response?.data?.messages?.errors)) {
    getErrorArray({
      errors: error.response.data.messages.errors,
      isValidationError: error.response?.status === 422,
      config,
    }).forEach((message) => {
      if (message === false) {
        return;
      }

      Notify.create({
        message,
        type: 'negative',
        timeout: 0,
      });
    });
  } else if (error?.response) {
    if (error?.response?.status === 429) {
      message = i18next.t('http_errors.too_many_requests_please_try_again_later', {
        ns: 'general',
      });
    } else if (/4[0-9]{2}/.test(error?.response?.status)) {
      if (/404/.test(error?.response?.status)) {
        message =
          error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          i18next.t('http_errors.requested_resource_was_not_found', { ns: 'general' });
      }

      if (/422/.test(error?.response?.status)) {
        message =
          error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          i18next.t('http_errors.unprocessible_entity_provided', { ns: 'general' });
      }

      if (/400/.test(error?.response?.status)) {
        message =
          error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          i18next.t('http_errors.bad_request', { ns: 'general' });
      }

      if (/401/.test(error?.response?.status)) {
        message =
          error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          i18next.t('http_errors.authentication_error', { ns: 'general' });
      }

      if (/403/.test(error?.response?.status)) {
        message =
          error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          i18next.t('http_errors.authorisation_error', { ns: 'general' });
      }

      if (error?.response?.status === 429) {
        message = i18next.t('http_errors.you_have_made_too_many_attempts_please_try_again_later', {
          ns: 'general',
        });
      }

      message =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        i18next.t('http_errors.unknown_resource_error_occurred', { ns: 'general' });
    } else if (/5[0-9]{2}/.test(error?.response?.status)) {
      message =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        i18next.t('http_errors.internal_server_error_occurred_if_persist_report', {
          ns: 'general',
        });
    } else if (error?.response?.data?.code === 'ER_DUP_ENTRY') {
      message = i18next.t('http_errors.this_resource_already_exist', { ns: 'general' });
    } else if (error?.response?.data?.error?.code == 'ENOTFOUND') {
      message = i18next.t(
        'http_errors.request_failed_seems_you_do_not_have_an_internet_connection',
        { ns: 'general' }
      );
    } else {
      message =
        typeof error?.response?.data?.error?.message === 'string'
          ? error?.response?.data?.error?.message
          : typeof error?.response?.data?.message === 'string'
          ? error?.response?.data?.message
          : '';
    }

    Notify.create({
      message,
      type: 'negative',
      timeout: 0,
    });

    return Promise.reject(error);
  } else {
    if (
      error?.message === 'Network Error' ||
      error?.message?.includes('ERR_CONNECTION_TIMED_OUT')
    ) {
      message = i18next.t('http_errors.local_network_error_occurred', { ns: 'general' });
    }

    if (error?.message === 'canceled') {
      message = i18next.t('http_errors.request_was_cancelled', { ns: 'general' });
    }

    if (error?.message?.includes('timeout')) {
      message = i18next.t('http_errors.request_was_cancelled', { ns: 'general' });
    }

    Notify.create({
      message: message ?? i18next.t('http_errors.unknown_error_occurred', { ns: 'general' }),
      type: 'negative',
      timeout: 0,
    });
  }

  return Promise.reject(error);
}

export async function httpRequestInterceptor(config) {
  // Impersonation header injection
  const isGlobalUser = () => !!store.getters['auth/isGlobalUser'];
  const resolvedOrgId = () => store.getters['auth/inStoreResolvedOrgId']();
  const GET_IMPERSONATION_SESSION = () => store.getters['auth/GET_IMPERSONATION_SESSION'];
  const isImpersonationActive = () => !!GET_IMPERSONATION_SESSION()?.active;
  const isImpersonableRequest = !!config.canBeImpersonated;

  if (config.notifiable) {
    config.headers['X-Is-Notifiable'] = 'On';
  }

  if (isGlobalUser() && isImpersonationActive() && isImpersonableRequest) {
    config.headers['X-Gotedo-Impersonation'] = 'on';
    config.headers['X-Gotedo-Impersonated-Organisation-Id'] = GET_IMPERSONATION_SESSION()?.churchId;
  }

  // Organisation Id inject for non-global users
  if (!isGlobalUser()) {
    config.headers['X-Gotedo-Organisation-Id'] = resolvedOrgId();
  }

  return config;
}

/**
 * @param {'v1' | 'v2'} version
 * @param {{
 * useProgressEvent?: boolean;
 * downloadProgressListener?: (progressEvent: ProgressEvent) => void;
 * uploadProgressListener?: (progressEvent: ProgressEvent) => void;
 * forFileDownload?: boolean
 * isMultipartData?: boolean
 * logoutOn404?: boolean
 * }=} extraConfig
 * @returns {import('axios').AxiosInstance}
 */
const instance = (version = 'v1', extraConfig = {}) => {
  const forFileDownload = extraConfig.forFileDownload;
  const useProgressEvent = extraConfig.useProgressEvent ?? forFileDownload;
  const config = httpConfig({ version });

  const axiosInstance = axios.create({
    ...config,
    headers: {
      ...config.headers,
      'Content-Type': extraConfig.isMultipartData ? 'multipart/form-data' : undefined,
    },
    responseType: forFileDownload ? 'arraybuffer' : undefined,
    // 60 minutes or as configured
    timeout: extraConfig.isMultipartData ? 60 * 60 * 1000 : config.timeout,
    onDownloadProgress(progressEvent) {
      if (!useProgressEvent) {
        return;
      }

      const listener = extraConfig.downloadProgressListener ?? downloadProgress;

      listener(progressEvent);
    },
    onUploadProgress(progressEvent) {
      if (!useProgressEvent) {
        return;
      }

      const listener = extraConfig.uploadProgressListener ?? uploadProgress;

      listener(progressEvent);
    },
    withCredentials: true,
    logoutOn404: !!extraConfig.logoutOn404,
  });

  axiosInstance.interceptors.response.use(httpResponseHandler, httpErrorHandler);

  axiosInstance.interceptors.request.use(httpRequestInterceptor);

  return axiosInstance;
};

/**
 * @typedef {{version: 'v1' | 'v2'; url?: string; route?: string; method: string, data?: Record<string, any>; query?: Record<string, any>; useProgressEvent?: boolean; downloadProgressListener: (progressEvent: ProgressEvent) => void; uploadProgressListener: (progressEvent: ProgressEvent) => void; signal: AbortSignal; timeout?: number; hideValidationErrors?: boolean, silent?: boolean, isMultipartData?: boolean; forFileDownload?: boolean; redirectToLogin?: boolean}} Payload
 * @param {Payload} payload
 * @returns {import('axios').AxiosPromise<any>}
 */
export function genericHttp(payload) {
  if (!payload) {
    throw new Error('payload is required');
  }

  let {
    version = 'v2',
    url,
    route,
    method = 'GET',
    data,
    query,
    silent = false,
    isMultipartData = false,
    notifiable,
  } = payload || {};
  if (route) {
    url = route;
  }

  method = method.toUpperCase();

  if (!url) {
    throw new Error('"url" is required');
  }

  const urlPrefix = `/${version}/`;

  const config = httpConfig({ version });
  notifiable =
    silent === true
      ? false
      : typeof notifiable !== 'undefined'
      ? !!notifiable
      : ['PUT', 'POST', 'DELETE', 'PATCH'].includes(method.toUpperCase());
  const hideValidationErrors = !!payload.hideValidationErrors;
  const forFileDownload = payload.forFileDownload;
  const useProgressEvent = payload.useProgressEvent ?? forFileDownload;
  const redirectToLogin = payload.redirectToLogin ?? true;

  axios.interceptors.response.use(httpResponseHandler, httpErrorHandler);
  axios.interceptors.request.use(httpRequestInterceptor);

  return axios({
    baseURL: config.baseURL,
    method,
    url: url.startsWith('http') ? url : url.replace(urlPrefix, ''),
    params: { ...(query || {}) },
    headers: {
      ...config.headers,
      'Content-Type': isMultipartData ? 'multipart/form-data' : undefined,
    },
    signal: payload.signal,
    data,
    // 60 minutes or as configured
    timeout: isMultipartData ? 60 * 60 * 1000 : payload.timeout ?? config.timeout,
    notifiable,
    hideValidationErrors,
    onUploadProgress: function (progressEvent) {
      if (!useProgressEvent) {
        return;
      }
      const listener = payload.uploadProgressListener ?? uploadProgress;
      listener(progressEvent);
    },

    // `onDownloadProgress` allows handling of progress events for downloads
    // browser only
    onDownloadProgress: function (progressEvent) {
      if (!useProgressEvent) {
        return;
      }
      const listener = payload.downloadProgressListener ?? downloadProgress;
      listener(progressEvent);
    },
    responseType: payload.forFileDownload ? 'arraybuffer' : undefined,
    // Send cookies
    withCredentials: true,
    redirectToLogin,
  });
}

export default instance;
